import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Loader2, Shield, Star, Mail, Clock, Weight, Ruler, Bell, Globe } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

const Cell = ({ label, value, mono = true }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono-num mb-1">{label}</div>
    <div className={`text-slate-900 ${mono ? "font-mono-num" : ""} text-sm break-words`}>
      {value === null || value === undefined || value === "" ? <span className="text-slate-400">—</span> : value}
    </div>
  </div>
);

export default function UserSnapshotDialog({ userId, email, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const res = await api.get(`/admin/users/${userId}/snapshot`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, userId]);

  const p = data?.profile;
  const counts = data?.counts || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="bg-white border border-slate-200 rounded-none text-slate-900 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="admin-user-snapshot-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-slate-200">
          <DialogTitle className="font-display uppercase tracking-tight text-xl flex items-center gap-3">
            <span className="text-[#0D9488]">// User Snapshot</span>
            <span className="text-slate-500 font-mono-num text-xs normal-case tracking-widest break-all">
              {email || p?.email || userId}
            </span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : !data ? (
          <div className="py-24 text-center text-slate-400 text-sm">No data.</div>
        ) : (
          <Tabs defaultValue="profile" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="bg-slate-50 border-b border-slate-200 rounded-none px-6 gap-1 h-auto justify-start flex-shrink-0" data-testid="snapshot-tabs">
              {[
                ["profile", "Profile"],
                ["shots", `Shots (${counts.shots || 0})`],
                ["vials", `Vials (${counts.vials || 0})`],
                ["cycles", `Cycles (${counts.cycles || 0})`],
                ["weights", `Weights (${counts.weights || 0})`],
                ["payments", `Payments (${counts.payments || 0})`],
              ].map(([v, l]) => (
                <TabsTrigger
                  key={v}
                  value={v}
                  className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-[#0D9488] data-[state=active]:border-b-2 data-[state=active]:border-[#0D9488] border-b-2 border-transparent text-slate-600 hover:text-slate-900 font-mono-num text-[11px] uppercase tracking-widest px-3 py-2"
                  data-testid={`snapshot-tab-${v}`}
                >
                  {l}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {/* PROFILE */}
              <TabsContent value="profile" className="mt-0 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  <Cell label="Email" value={<span className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-500" />{p.email}</span>} mono={false} />
                  <Cell label="Display name" value={p.name} mono={false} />
                  <Cell label="Role" value={
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-slate-300 text-[10px] uppercase tracking-widest">
                      <Shield className="w-3 h-3" /> {p.role}
                    </span>
                  } mono={false} />
                  <Cell label="Premium" value={
                    p.has_premium
                      ? <span className="inline-flex items-center gap-1 text-[#0D9488]"><Star className="w-3 h-3 fill-[#0D9488]" /> Member</span>
                      : <span className="text-slate-500">Free</span>
                  } mono={false} />
                  <Cell label="Terms accepted" value={p.terms_accepted ? "Yes" : "No"} />
                  <Cell label="Timezone" value={<span className="flex items-center gap-1.5"><Globe className="w-3 h-3 text-slate-500" />{p.timezone || "—"}</span>} mono={false} />
                  <Cell label="Body weight" value={p.body_weight_kg ? <span className="flex items-center gap-1.5"><Weight className="w-3 h-3 text-slate-500" />{p.body_weight_kg} kg</span> : null} mono={false} />
                  <Cell label="Goal weight" value={p.goal_weight_kg ? `${p.goal_weight_kg} kg` : null} />
                  <Cell label="Height" value={p.height_cm ? <span className="flex items-center gap-1.5"><Ruler className="w-3 h-3 text-slate-500" />{p.height_cm} cm</span> : null} mono={false} />
                  <Cell label="Default interval" value={p.default_interval_hours ? `${p.default_interval_hours} h` : null} />
                  <Cell label="Notifications" value={<span className="flex items-center gap-1.5"><Bell className="w-3 h-3 text-slate-500" />{p.notifications_enabled ? "On" : "Off"}</span>} mono={false} />
                  <Cell label="Registered" value={<span className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-slate-500" />{fmtDate(p.created_at)}</span>} mono={false} />
                  <Cell label="Last active" value={fmtDate(p.last_seen_at)} />
                </div>
                {p.bio && (
                  <div className="border border-slate-200 bg-slate-50 p-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono-num mb-2">Bio</div>
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{p.bio}</div>
                  </div>
                )}
              </TabsContent>

              {/* SHOTS */}
              <TabsContent value="shots" className="mt-0">
                {data.shots.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 text-sm">No shots logged.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
                      <tr className="border-b border-slate-200"><th className="text-left px-2 py-2 font-normal">Peptide</th><th className="text-right px-2 py-2 font-normal">Dose</th><th className="text-left px-2 py-2 font-normal">Site</th><th className="text-left px-2 py-2 font-normal">Taken</th><th className="text-left px-2 py-2 font-normal">Notes</th></tr>
                    </thead>
                    <tbody>
                      {data.shots.map((s) => (
                        <tr key={s.id} className="border-b border-slate-200/60 hover:bg-slate-50/30">
                          <td className="px-2 py-2 text-slate-900">{s.peptide_name}{s.demo && <span className="ml-1 text-[9px] uppercase tracking-widest text-slate-400">demo</span>}</td>
                          <td className="px-2 py-2 text-right font-mono-num text-slate-900 tabular-nums">{s.dose_amount} {s.dose_unit}</td>
                          <td className="px-2 py-2 text-slate-800">{s.injection_site || "—"}</td>
                          <td className="px-2 py-2 text-slate-600 font-mono-num text-xs">{fmtDate(s.taken_at)}</td>
                          <td className="px-2 py-2 text-slate-500 text-xs max-w-xs truncate" title={s.notes || ""}>{s.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>

              {/* VIALS */}
              <TabsContent value="vials" className="mt-0">
                {data.vials.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 text-sm">No vials.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
                      <tr className="border-b border-slate-200"><th className="text-left px-2 py-2 font-normal">Peptide</th><th className="text-right px-2 py-2 font-normal">Amount</th><th className="text-right px-2 py-2 font-normal">Remaining</th><th className="text-right px-2 py-2 font-normal">BAC (mL)</th><th className="text-left px-2 py-2 font-normal">Expires</th><th className="text-left px-2 py-2 font-normal">Created</th></tr>
                    </thead>
                    <tbody>
                      {data.vials.map((v) => (
                        <tr key={v.id} className="border-b border-slate-200/60 hover:bg-slate-50/30">
                          <td className="px-2 py-2 text-slate-900">{v.peptide_name}</td>
                          <td className="px-2 py-2 text-right font-mono-num text-slate-900 tabular-nums">{v.amount_mg} mg</td>
                          <td className="px-2 py-2 text-right font-mono-num tabular-nums text-[#0D9488]">{v.remaining_mg ?? "—"} mg</td>
                          <td className="px-2 py-2 text-right font-mono-num text-slate-800 tabular-nums">{v.bac_water_ml ?? "—"}</td>
                          <td className="px-2 py-2 text-slate-600 font-mono-num text-xs">{fmtDateOnly(v.expiration_date)}</td>
                          <td className="px-2 py-2 text-slate-600 font-mono-num text-xs">{fmtDateOnly(v.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>

              {/* CYCLES */}
              <TabsContent value="cycles" className="mt-0">
                {data.cycles.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 text-sm">No cycles.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
                      <tr className="border-b border-slate-200"><th className="text-left px-2 py-2 font-normal">Peptide</th><th className="text-right px-2 py-2 font-normal">Dose</th><th className="text-left px-2 py-2 font-normal">Freq.</th><th className="text-left px-2 py-2 font-normal">Range</th><th className="text-left px-2 py-2 font-normal">Active</th></tr>
                    </thead>
                    <tbody>
                      {data.cycles.map((c) => (
                        <tr key={c.id} className="border-b border-slate-200/60 hover:bg-slate-50/30">
                          <td className="px-2 py-2 text-slate-900">{c.peptide_name}</td>
                          <td className="px-2 py-2 text-right font-mono-num text-slate-900 tabular-nums">{c.dose_amount} {c.dose_unit}</td>
                          <td className="px-2 py-2 text-slate-800 font-mono-num text-xs">{c.frequency || "—"}</td>
                          <td className="px-2 py-2 text-slate-600 font-mono-num text-xs">{fmtDateOnly(c.start_date)} → {fmtDateOnly(c.end_date)}</td>
                          <td className="px-2 py-2">
                            {c.active
                              ? <span className="inline-block px-2 py-0.5 border border-emerald-500/40 text-emerald-400 text-[10px] uppercase tracking-widest font-mono-num">Active</span>
                              : <span className="inline-block px-2 py-0.5 border border-slate-200 text-slate-500 text-[10px] uppercase tracking-widest font-mono-num">Ended</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>

              {/* WEIGHTS */}
              <TabsContent value="weights" className="mt-0">
                {data.weights.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 text-sm">No weight logs.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
                      <tr className="border-b border-slate-200"><th className="text-left px-2 py-2 font-normal">Date</th><th className="text-right px-2 py-2 font-normal">Weight</th></tr>
                    </thead>
                    <tbody>
                      {data.weights.map((w) => (
                        <tr key={w.id} className="border-b border-slate-200/60 hover:bg-slate-50/30">
                          <td className="px-2 py-2 text-slate-600 font-mono-num text-xs">{fmtDate(w.created_at)}</td>
                          <td className="px-2 py-2 text-right font-mono-num tabular-nums text-[#0D9488]">{w.weight_kg} kg</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>

              {/* PAYMENTS */}
              <TabsContent value="payments" className="mt-0">
                {data.payments.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 text-sm">No payments.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
                      <tr className="border-b border-slate-200"><th className="text-left px-2 py-2 font-normal">Date</th><th className="text-right px-2 py-2 font-normal">Amount</th><th className="text-left px-2 py-2 font-normal">Mode</th><th className="text-left px-2 py-2 font-normal">Status</th><th className="text-left px-2 py-2 font-normal">Capture</th></tr>
                    </thead>
                    <tbody>
                      {data.payments.map((pmt) => (
                        <tr key={pmt.id} className="border-b border-slate-200/60 hover:bg-slate-50/30">
                          <td className="px-2 py-2 text-slate-600 font-mono-num text-xs">{fmtDate(pmt.created_at)}</td>
                          <td className="px-2 py-2 text-right font-mono-num tabular-nums text-[#0D9488]">${Number(pmt.amount_usd || 0).toFixed(2)}</td>
                          <td className="px-2 py-2 text-slate-800 font-mono-num text-xs uppercase">{pmt.mode || "—"}</td>
                          <td className="px-2 py-2 text-slate-800 font-mono-num text-xs">{pmt.status || "—"}{pmt.refunded_at && <span className="ml-1 text-red-400">(refunded)</span>}</td>
                          <td className="px-2 py-2 text-slate-500 font-mono-num text-[10px] break-all">{pmt.capture_id || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
